<!--
  @SheetFields  Reusable input snippets for the character sheets (Classical / parchment look).
  Usage:  import {field, note, textblock, pips, stepper, check, choice} from "./SheetFields.svelte"
          {@render field({id: 'race', label: "Volk", value: character.race})}
  Change events bubble, so a surrounding `onchange={change_attribute.bind(obj)}` picks them up via the input's id.
-->

<script module>
	export {field, note, textblock, pips, check, choice}

	export const signed = v => (v > 0) ? `+${v}` : (v < 0) ? `–${-v}` : "0"

	function blur_on_enter(e) {
		if (e.key === 'Enter') {
			e.preventDefault()
			e.target.blur()
		}
	}
</script>


<!-------------------------------- SNIPPETS ---------------------------------->

<!-- labelled single-line input with an underline, optional unit, spanning `span` grid columns -->
{#snippet field({id, label, value, unit = "", span = 1, disabled = false, placeholder = "", numeric = false, onchange})}
	<label class="field" class:span2={span === 2} class:span3={span === 3}>
		<label for={id} class="kicker">{label}</label>
		<span class="field-line">
			<input class="field-input" class:numeric {id} value={value ?? ""} {placeholder} {disabled}
			       inputmode={numeric ? 'numeric' : undefined} {onchange} onkeydown={blur_on_enter}/>
			{#if unit}<span class="field-unit">{unit}</span>{/if}
		</span>
	</label>
{/snippet}

<!-- unlabelled, italic inline input for comments in table rows -->
{#snippet note({id = 'comment', value, disabled = false, placeholder = "—", onchange})}
	<input class="note" {id} value={value ?? ""} {placeholder} {disabled} {onchange} onkeydown={blur_on_enter}/>
{/snippet}

<!-- titled long text: two justified columns to read, a textarea while editing -->
{#snippet textblock({id, title, value, editing = false, disabled = false, ontoggle})}
	<div class="textblock" data-help={id}>
		<div class="textblock-head">
			<h2 data-help={id} class="textblock-title">{title}</h2>
			<div class="textblock-rule"></div>
			{#if !disabled}
				<button class="btn-outline" onclick={ontoggle}>
					<span class="fa icon-pen"></span>{editing ? "Fertig" : "Bearbeiten"}
				</button>
			{/if}
		</div>
		{#if editing}
			<textarea class="textblock-edit" {id} rows=9>{value ?? ""}</textarea>
		{:else}
			<!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_noninteractive_element_interactions -->
			<p class="textblock-read" class:editable={!disabled} lang="de" onclick={disabled ? null : ontoggle}>{value ?? ""}</p>
		{/if}
	</div>
{/snippet}

<!-- scale of nine boxes from −4 to +4; filled from the centre to the current value -->
{#snippet pips({value, disabled = false, onselect})}
	{@const val = value ?? 0}
	<span class="pips">
		{#each [-4, -3, -2, -1, 0, 1, 2, 3, 4] as p}
			{@const filled = (val > 0 && p > 0 && p <= val) || (val < 0 && p < 0 && p >= val)}
			<button class="pip" class:zero={p === 0} class:pos={filled && p > 0} class:neg={filled && p < 0}
			        title={signed(p)} {disabled} onclick={() => onselect(p)}>
				{#if p === 0 && val === 0}●{/if}
			</button>
		{/each}
	</span>
{/snippet}

<!-- −/+ control for raising a value while upgrading, with the cost of the next step -->
{#snippet stepper({value = 0, cost, disabled = false, ondec, oninc})}
	<span class="stepper">
		<button class="step" disabled={disabled || value <= 0} onclick={ondec}>−</button>
		<span class="step-value num">{value ? `+${value}` : "·"}</span>
		<button class="step" {disabled} onclick={oninc}>+</button>
		{#if cost !== undefined}<span class="step-cost num">({cost} EP)</span>{/if}
	</span>
{/snippet}

<!-- checkbox chip (e.g. abilities); `kind` (hurt, heal, …) sets --hue, which tints border and text -->
{#snippet check({id, label, kind = "", checked = false, disabled = false, dimmed = false, title = "", onchange, onlabel})}
	<label class="check {kind}" class:checked class:dimmed class:locked={disabled} {title}>
		<input type="checkbox" {id} {checked} {disabled} {onchange}/>
		<a href="#{label}" onclick={onlabel}>{label}</a>
	</label>
{/snippet}

<!-- underlined select; options as [{ID, name}] -->
{#snippet choice({id, value = "", options = [], placeholder = "Auswählen …", disabled = false, onchange})}
	<select class="choice" {id} {value} {disabled} {onchange}>
		<option value="">{placeholder}</option>
		{#each options as opt}
			<option value={opt.ID}>{opt.name}</option>
		{/each}
	</select>
{/snippet}


<!-------------------------------- STYLES -------------------------------------->

<style lang="sass">
	/* field */
	.field
		display: flex
		flex-direction: column
		gap: 2px

		&.span2
			grid-column: span 2

		&.span3
			grid-column: span 3

	.field-line
		display: flex
		align-items: baseline
		gap: 6px
		border-bottom: 1px solid var(--color-neutral-400)

	.field-input
		flex: 1
		min-width: 0
		padding: 4px 0
		border: none
		background: transparent
		font-family: var(--font-body)
		font-size: 17px
		color: var(--color-text)
		outline: none

		&.numeric
			font-variant-numeric: tabular-nums

		&:focus
			background: var(--focus-tint)

		&:disabled
			color: var(--color-text)

	.field-unit
		font-size: 13px
		font-style: italic
		color: var(--color-neutral-600)

	/* note */
	.note
		width: 100%
		padding: 8px 0
		border: none
		background: transparent
		font-family: var(--font-body)
		font-size: 15px
		font-style: italic
		color: var(--color-neutral-800)
		outline: none

		&:focus
			background: var(--focus-tint)

		&:disabled
			color: var(--color-neutral-800)

		&::placeholder
			color: var(--color-neutral-500)

	/* textblock */
	.textblock
		margin-top: 44px

	.textblock-head
		display: flex
		align-items: center
		gap: 16px
		margin: 1em 0 16px 0

	.textblock-title
		font-weight: 400
		font-size: 32px

	.textblock-rule
		flex: 1
		height: 1px
		background: var(--color-accent-300)

	.textblock-edit
		display: block
		width: 100%
		padding: 14px 16px
		border: 1px solid var(--color-accent-300)
		border-radius: var(--radius-md)
		background: color-mix(in srgb, var(--color-accent-100) 55%, transparent)
		font-family: var(--font-body)
		font-size: 16px
		line-height: 1.7
		color: var(--color-text)
		resize: vertical
		outline: none

	.textblock-read
		column-count: 2
		column-gap: 44px
		column-rule: 1px solid var(--color-divider)
		text-align: justify
		hyphens: auto
		font-size: 16px
		line-height: 1.7
		white-space: pre-line

		&.editable
			cursor: text

	/* pips */
	.pips
		display: flex
		gap: 3px
		justify-content: center

	.pip
		width: 20px
		height: 20px
		padding: 0
		border: 1px solid var(--color-accent-300)
		border-radius: var(--radius-sm)
		background: transparent
		font-size: 9px
		line-height: 1
		color: var(--color-accent-700)
		cursor: pointer

		&:hover:not(:disabled)
			background: var(--color-accent-200)

		&:disabled
			cursor: default

		&.zero
			border-radius: 50%
			border-color: var(--color-accent-600)

		&.pos
			border-color: var(--color-accent-600)
			background: var(--color-accent-400)

		&.neg
			border-color: var(--color-neutral-700)
			background: var(--color-neutral-500)

	/* stepper */
	.stepper
		display: flex
		align-items: center
		justify-content: center
		gap: 6px

	.step
		width: 22px
		height: 22px
		padding: 0
		border: 1px solid var(--color-accent)
		border-radius: var(--radius-sm)
		background: transparent
		color: var(--color-accent-800)
		line-height: 1
		cursor: pointer

		&:hover:not(:disabled)
			background: var(--color-accent-200)

		&:disabled
			opacity: .45
			cursor: default

	.step-value
		min-width: 22px
		text-align: center
		font-weight: 600
		color: var(--color-accent-800)

	.step-cost
		font-size: 11px
		color: var(--color-neutral-600)

	/* check chip */
	.check
		--chip: hsl(var(--hue, 30) 58% 32%)
		display: inline-flex
		align-items: center
		gap: 8px
		padding: 5px 12px
		border: 1px dashed var(--color-neutral-400)
		border-radius: var(--radius-md)
		color: var(--color-neutral-700)
		font-size: 14px
		cursor: pointer

		&.checked
			border: 1px solid var(--chip)
			background: color-mix(in srgb, var(--chip) 9%, transparent)
			color: var(--chip)

		&.locked
			cursor: default

		&.dimmed
			opacity: .45

		input
			appearance: none
			width: 13px
			height: 13px
			margin: 0
			border: 1.5px solid var(--chip)
			border-radius: 2px
			background: transparent
			cursor: inherit

			&:checked
				background: var(--chip) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath d='M2.5 6.2 5 8.6 9.6 3.4' fill='none' stroke='white' stroke-width='1.8'/%3E%3C/svg%3E") center / 100% no-repeat

	/* choice */
	.choice
		min-width: 260px
		padding: 4px 2px
		border: none
		border-bottom: 1px solid var(--color-neutral-400)
		background: transparent
		font-family: var(--font-body)
		font-size: 15px
		color: var(--color-text)
</style>
